import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import VideoPlayer from './VideoPlayer';
import {
    generateCalendarData,
    calculateProgressStats,
    calculateTotalCompletedDays,
    calculateTotalPointsEarned,
    calculateChallengeSuccessRate,
    formatValue,
    getMaxValue,
    getStepSize,
    isWithinGracePeriod,
    isPastOrToday,
} from '@fitzaa/shared';
import { EnhancedProgressMetricsWeb } from './EnhancedProgressMetricsWeb';
import { ChallengeProgressViewProps } from '../types/challenge';

export const ChallengeProgressView: React.FC<ChallengeProgressViewProps> = ({
    challenge,
    progressData,
    onUpdateProgress,
    onDropChallenge,
    onViewLeaderboard,
}) => {
    const { theme } = useTheme();

    // Get today's specific value for the progress update interface
    const getTodayValue = useCallback(() => {
        const today = new Date().toISOString().split('T')[0];
        const todayCompletion = progressData?.dailyCompletions?.[today];
        return todayCompletion?.value || 0;
    }, [progressData?.dailyCompletions]);

    const [selectedValue, setSelectedValue] = useState(() => {
        const today = new Date().toISOString().split('T')[0];
        const todayCompletion = progressData?.dailyCompletions?.[today];
        return todayCompletion?.value || 0;
    });
    const [isUpdating, setIsUpdating] = useState(false);
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [showDayDetail, setShowDayDetail] = useState(false);
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);

    // Update selectedValue when progressData changes to reflect today's actual value
    useEffect(() => {
        const todayValue = getTodayValue();
        setSelectedValue(todayValue);
    }, [getTodayValue]);

    // New state for text input functionality
    const [showTextInput, setShowTextInput] = useState(false);
    const [textInputValue, setTextInputValue] = useState('');
    const [showDayTextInput, setShowDayTextInput] = useState(false);
    const [dayTextInputValue, setDayTextInputValue] = useState('');

    // Video player state
    const [showVideoPlayer, setShowVideoPlayer] = useState(false);
    const [hasShownWelcomeVideo, setHasShownWelcomeVideo] = useState(false);

    // Video player handlers
    const handleVideoPlay = () => {
        setShowVideoPlayer(true);
    };

    const handleVideoClose = () => {
        setShowVideoPlayer(false);
    };

    // Check if this is first time user and show welcome video
    const challengeVideoUrl = (challenge as any)?.videoUrl;
    useEffect(() => {
        if (challengeVideoUrl && !hasShownWelcomeVideo && progressData?.currentValue === 0) {
            // Show welcome video for first-time users
            const timer = setTimeout(() => {
                console.log('🎬 Showing welcome video for first-time challenge user');
                setShowVideoPlayer(true);
                setHasShownWelcomeVideo(true);
            }, 1000); // 1 second delay

            return () => clearTimeout(timer);
        }
    }, [challengeVideoUrl, hasShownWelcomeVideo, progressData?.currentValue]);

    const calendarData = generateCalendarData(challenge, progressData as any, currentMonth);
    calculateProgressStats(calendarData);

    // Calculate total completed days across entire challenge period (not just current month)
    calculateTotalCompletedDays(challenge, progressData as any);

    // Calculate points earned and success rate for the entire challenge period
    calculateTotalPointsEarned(challenge, progressData as any);
    calculateChallengeSuccessRate(challenge, progressData as any);

    // Handle text input for today's progress
    const handleTextInputSubmit = () => {
        const value = parseFloat(textInputValue);
        const targetValue = (challenge as any).measurementTarget || challenge.targetValue;
        if (!isNaN(value) && value >= 0 && value <= getMaxValue(challenge.metric, targetValue)) {
            setSelectedValue(value);
            setShowTextInput(false);
            setTextInputValue('');
        } else {
            alert(`Please enter a valid number between 0 and ${getMaxValue(challenge.metric, targetValue)}`);
        }
    };

    // Handle text input for previous days
    const handleDayTextInputSubmit = () => {
        const value = parseFloat(dayTextInputValue);
        const targetValue = (challenge as any).measurementTarget || challenge.targetValue;
        if (!isNaN(value) && value >= 0 && value <= targetValue * 2) {
            setShowDayTextInput(false);
            setDayTextInputValue('');
            return value;
        } else {
            alert(`Please enter a valid number between 0 and ${targetValue * 2}`);
            return null;
        }
    };

    // Validate text input to only allow numbers
    const validateTextInput = (text: string) => {
        // Only allow numbers and decimal points
        const numericRegex = /^[0-9]*\.?[0-9]*$/;
        if (numericRegex.test(text) || text === '') {
            return text;
        }
        return textInputValue; // Keep previous value if invalid
    };

    const validateDayTextInput = (text: string) => {
        // Only allow numbers and decimal points
        const numericRegex = /^[0-9]*\.?[0-9]*$/;
        if (numericRegex.test(text) || text === '') {
            return text;
        }
        return dayTextInputValue; // Keep previous value if invalid
    };

    const handlePreviousMonth = () => {
        setCurrentMonth(prev => {
            const newMonth = new Date(prev);
            newMonth.setMonth(prev.getMonth() - 1);
            return newMonth;
        });
    };

    const handleNextMonth = () => {
        setCurrentMonth(prev => {
            const newMonth = new Date(prev);
            newMonth.setMonth(prev.getMonth() + 1);
            return newMonth;
        });
    };

    const handleUpdateProgress = async () => {
        if (isUpdating) return;

        setIsUpdating(true);
        try {
            await onUpdateProgress(selectedValue);
        } catch (error) {
            console.error('Failed to update progress:', error);
        } finally {
            setIsUpdating(false);
        }
    };

    const handleDayClick = (day: any) => {
        // Only allow clicks on non-padding days that are in the past or today
        if (day.isPadding || !isPastOrToday(new Date(day.date + 'T00:00:00'))) {
            return;
        }

        const clickedDate = new Date(day.date + 'T00:00:00');

        // Check if the day is within the grace period (5 days in the past)
        if (isWithinGracePeriod(clickedDate)) {
            setSelectedDate(clickedDate);
            setShowDayDetail(true);
        } else {
            alert('This day is outside the 5-day grace period for editing past entries.');
        }
    };

    const handleSaveDayProgress = async (date: Date, value: number) => {
        try {
            // Call the update function with the specific date
            const dateString = date.toISOString().split('T')[0];
            await onUpdateProgress(value, dateString);

            // Close the day detail view
            setShowDayDetail(false);
            setSelectedDate(null);
        } catch (error) {
            console.error('Failed to save day progress:', error);
            throw error;
        }
    };

    const handleCancelDayDetail = () => {
        setShowDayDetail(false);
        setSelectedDate(null);
    };

    const getExistingValueForDate = (date: Date): number => {
        const dateKey = date.toISOString().split('T')[0];
        return progressData.dailyCompletions?.[dateKey]?.value || 0;
    };



    // Detect mobile screen size
    const [isMobile, setIsMobile] = React.useState(window.innerWidth < 768);
    
    React.useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return (
        <div style={{ 
            padding: isMobile ? '8px' : '20px', 
            maxWidth: isMobile ? '100%' : '900px', 
            margin: isMobile ? '0' : '0 auto',
            width: '100%',
            boxSizing: 'border-box'
        }}>
            {/* Ultra-Posh Header */}
            <div style={{
                background: `linear-gradient(135deg, ${theme.colors.primary}15 0%, ${theme.colors.primary}05 100%)`,
                borderRadius: isMobile ? '12px' : '24px',
                padding: isMobile ? '12px 16px' : '32px',
                marginBottom: isMobile ? '12px' : '32px',
                border: `1px solid ${theme.colors.primary}20`,
                position: 'relative',
                overflow: 'hidden'
            }}>
                {/* Decorative Background Elements */}
                <div style={{
                    position: 'absolute',
                    top: '-50px',
                    right: '-50px',
                    width: '200px',
                    height: '200px',
                    background: `radial-gradient(circle, ${theme.colors.primary}10 0%, transparent 70%)`,
                    borderRadius: '50%'
                }} />
                <div style={{
                    position: 'absolute',
                    bottom: '-30px',
                    left: '-30px',
                    width: '120px',
                    height: '120px',
                    background: `radial-gradient(circle, ${theme.colors.primary}08 0%, transparent 70%)`,
                    borderRadius: '50%'
                }} />

                <div style={{ position: 'relative', zIndex: 1 }}>
                    <div style={{
                        display: 'flex',
                        flexDirection: isMobile ? 'column' : 'row',
                        alignItems: isMobile ? 'flex-start' : 'center',
                        justifyContent: 'space-between',
                        marginBottom: '16px',
                        gap: isMobile ? '12px' : '0'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '8px' : '12px', flex: 1, minWidth: 0 }}>
                            {(challenge as any)?.emoji && (
                                <span style={{ fontSize: isMobile ? '20px' : '32px', flexShrink: 0 }}>{(challenge as any).emoji}</span>
                            )}
                            <div style={{ minWidth: 0, flex: 1 }}>
                                <h1 style={{
                                    fontSize: isMobile ? theme.typography.h3.fontSize : theme.typography.h1.fontSize,
                                    fontWeight: theme.typography.h1.fontWeight,
                                    color: theme.colors.text,
                                    marginBottom: isMobile ? '4px' : '8px',
                                    letterSpacing: theme.typography.h1.letterSpacing,
                                    wordWrap: 'break-word',
                                    overflowWrap: 'break-word',
                                    lineHeight: isMobile ? '1.3' : theme.typography.h1.lineHeight,
                                    fontFamily: theme.fonts.display
                                }}>
                                    {challenge.title}
                                </h1>
                                <p style={{
                                    fontSize: isMobile ? theme.typography.caption.fontSize : theme.typography.body.fontSize,
                                    color: theme.colors.textSecondary,
                                    fontWeight: theme.typography.body.fontWeight,
                                    opacity: 0.9,
                                    wordWrap: 'break-word',
                                    overflowWrap: 'break-word',
                                    lineHeight: isMobile ? '1.4' : theme.typography.body.lineHeight,
                                    fontFamily: theme.fonts.secondary
                                }}>
                                    {challenge.description}
                                </p>
                            </div>
                        </div>

                        {/* Clean Media Play Button (Video or Audio) */}
                        {((challenge as any)?.videoUrl || (challenge as any)?.audioUrl) && (
                            <button
                                onClick={handleVideoPlay}
                                className="inline-flex items-center justify-center bg-gradient-to-r from-indigo-500 to-sky-500 dark:from-indigo-500 dark:to-sky-500 w-9 h-9 rounded-full shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105 text-base text-white"
                                title={(challenge as any)?.videoUrl ? 'Watch Video' : 'Listen to Audio'}
                                style={{ flexShrink: 0 }}
                            >
                                <span style={{ transform: 'translateX(1px)' }}>
                                    {(challenge as any)?.videoUrl ? '▶️' : '🎵'}
                                </span>
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Clean Challenge Timeline */}
            <div style={{
                backgroundColor: theme.colors.surface,
                borderRadius: '12px',
                padding: isMobile ? '12px' : '20px',
                marginBottom: isMobile ? '12px' : '24px',
                border: `1px solid ${theme.colors.border}`,
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                position: 'relative'
            }}>

                <div style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: isMobile ? '4px' : '0',
                    position: 'relative'
                }}>
                    {/* Start Date - Clean */}
                    <div style={{
                        textAlign: 'center',
                        flex: 1,
                        minWidth: 0
                    }}>
                        <div style={{
                            width: isMobile ? '35px' : '60px',
                            height: isMobile ? '35px' : '60px',
                            backgroundColor: theme.colors.primary,
                            borderRadius: '50%',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: '0 auto 4px',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                        }}>
                            <div style={{
                                fontSize: isMobile ? '6px' : '8px',
                                fontWeight: '600',
                                letterSpacing: '0.5px',
                                color: '#ffffff',
                                opacity: 0.9
                            }}>
                                START
                            </div>
                            <div style={{
                                fontSize: isMobile ? '10px' : '14px',
                                fontWeight: '700',
                                color: '#ffffff',
                                marginTop: '1px'
                            }}>
                                {new Date(challenge.startDate).getDate()}
                            </div>
                        </div>
                        <div style={{
                            fontSize: isMobile ? '8px' : '12px',
                            fontWeight: '500',
                            color: theme.colors.text,
                            lineHeight: '1.2'
                        }}>
                            {new Date(challenge.startDate).toLocaleDateString('en-US', {
                                month: 'short',
                                year: 'numeric'
                            })}
                        </div>
                    </div>

                    {/* Timeline Connector - Clean */}
                    <div style={{
                        flex: isMobile ? '0.5' : 2,
                        height: '2px',
                        backgroundColor: theme.colors.border,
                        margin: isMobile ? '0 2px' : '0 16px',
                        borderRadius: '1px',
                        minWidth: isMobile ? '8px' : 'auto'
                    }} />

                    {/* End Date - Clean */}
                    <div style={{
                        textAlign: 'center',
                        flex: 1,
                        minWidth: 0
                    }}>
                        <div style={{
                            width: isMobile ? '35px' : '60px',
                            height: isMobile ? '35px' : '60px',
                            backgroundColor: theme.colors.primary,
                            borderRadius: '50%',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: '0 auto 4px',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                        }}>
                            <div style={{
                                fontSize: isMobile ? '6px' : '8px',
                                fontWeight: '600',
                                letterSpacing: '0.5px',
                                color: '#ffffff',
                                opacity: 0.9
                            }}>
                                END
                            </div>
                            <div style={{
                                fontSize: isMobile ? '10px' : '14px',
                                fontWeight: '700',
                                color: '#ffffff',
                                marginTop: '1px'
                            }}>
                                {new Date(challenge.endDate).getDate()}
                            </div>
                        </div>
                        <div style={{
                            fontSize: isMobile ? '8px' : '12px',
                            fontWeight: '500',
                            color: theme.colors.text,
                            lineHeight: '1.2'
                        }}>
                            {new Date(challenge.endDate).toLocaleDateString('en-US', {
                                month: 'short',
                                year: 'numeric'
                            })}
                        </div>
                    </div>

                    {/* Total Days Badge - Clean */}
                    <div style={{
                        flex: isMobile ? '0.8' : 'none',
                        backgroundColor: theme.colors.primary,
                        borderRadius: isMobile ? '6px' : '8px',
                        padding: isMobile ? '4px 6px' : '8px 12px',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                        textAlign: 'center',
                        minWidth: isMobile ? '50px' : 'auto'
                    }}>
                        <div style={{
                            fontSize: isMobile ? '6px' : '8px',
                            fontWeight: '600',
                            letterSpacing: '0.5px',
                            color: '#ffffff',
                            opacity: 0.9,
                            marginBottom: '1px'
                        }}>
                            TOTAL
                        </div>
                        <div style={{
                            fontSize: isMobile ? '9px' : '14px',
                            fontWeight: '700',
                            color: '#ffffff',
                            textAlign: 'center',
                            lineHeight: '1.2'
                        }}>
                            {Math.ceil((new Date(challenge.endDate).getTime() - new Date(challenge.startDate).getTime()) / (1000 * 60 * 60 * 24)) + 1} DAYS
                        </div>
                    </div>
                </div>
            </div>

            {/* Clean Enhanced Progress Metrics */}
            {challenge && progressData && (
                <div style={{
                    backgroundColor: theme.colors.surface,
                    borderRadius: '12px',
                    padding: isMobile ? '12px' : '16px',
                    marginBottom: isMobile ? '12px' : '16px',
                    border: `1px solid ${theme.colors.border}`,
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
                }}>
                    <h2 style={{
                        fontSize: isMobile ? '11px' : '14px',
                        fontWeight: '600',
                        color: theme.colors.text,
                        marginBottom: isMobile ? '8px' : '12px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                    }}>
                        Progress Analytics
                    </h2>
                    <EnhancedProgressMetricsWeb
                        challenge={challenge}
                        progressData={progressData}
                        showDetailedBreakdown={true}
                        className="enhanced-metrics-clean"
                    />
                </div>
            )}





            {/* Value Selector */}
            {/* Clean Progress Update Section */}
            <div style={{
                backgroundColor: theme.colors.surface,
                borderRadius: '12px',
                padding: isMobile ? '12px' : '20px',
                marginBottom: isMobile ? '12px' : '24px',
                border: `1px solid ${theme.colors.border}`,
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
            }}>
                <h3 style={{
                    fontSize: isMobile ? '14px' : '18px',
                    fontWeight: '600',
                    color: theme.colors.text,
                    marginBottom: isMobile ? '10px' : '16px'
                }}>
                    Update Today's Progress
                </h3>

                {/* Clean Value Selector */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: isMobile ? '8px' : '12px',
                    marginBottom: isMobile ? '12px' : '16px'
                }}>
                    {/* Minus Button */}
                    <button
                        style={{
                            width: isMobile ? '40px' : '48px',
                            height: isMobile ? '40px' : '48px',
                            borderRadius: '8px',
                            border: 'none',
                            backgroundColor: theme.colors.primary,
                            color: 'white',
                            fontSize: isMobile ? '18px' : '20px',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                            transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-1px)';
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                        }}
                        onClick={() => setSelectedValue(Math.max(0, selectedValue - getStepSize(challenge.metric)))}
                    >
                        −
                    </button>

                    {/* Value Display */}
                    <div style={{
                        flex: 1,
                        padding: isMobile ? '10px' : '12px',
                        borderRadius: '8px',
                        border: `1px solid ${theme.colors.border}`,
                        backgroundColor: theme.colors.background,
                        textAlign: 'center'
                    }}>
                        <span style={{
                            fontSize: isMobile ? '14px' : '18px',
                            fontWeight: '600',
                            color: theme.colors.text
                        }}>
                            {formatValue(selectedValue, challenge.metric, (challenge as any).measurementUnit || challenge.targetUnit)}
                        </span>
                    </div>

                    {/* Plus Button */}
                    <button
                        style={{
                            width: isMobile ? '40px' : '48px',
                            height: isMobile ? '40px' : '48px',
                            borderRadius: '8px',
                            border: 'none',
                            backgroundColor: theme.colors.primary,
                            color: 'white',
                            fontSize: isMobile ? '18px' : '20px',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                            transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-1px)';
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                        }}
                        onClick={() => setSelectedValue(Math.min(getMaxValue(challenge.metric, (challenge as any).measurementTarget || challenge.targetValue), selectedValue + getStepSize(challenge.metric)))}
                    >
                        +
                    </button>

                    {/* Clean Text Input Toggle Button */}
                    <button
                        style={{
                            width: isMobile ? '40px' : '48px',
                            height: isMobile ? '40px' : '48px',
                            borderRadius: '8px',
                            border: 'none',
                            backgroundColor: theme.colors.secondary,
                            color: 'white',
                            fontSize: isMobile ? '14px' : '16px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                            transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-1px)';
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                        }}
                        onClick={() => {
                            if (showTextInput) {
                                setShowTextInput(false);
                                setTextInputValue('');
                            } else {
                                setShowTextInput(true);
                                setTextInputValue(selectedValue.toString());
                            }
                        }}
                        title={showTextInput ? 'Switch to +/- buttons' : 'Enter value manually'}
                    >
                        {showTextInput ? '↺' : '✏️'}
                    </button>
                </div>

                {/* Ultra-Posh Text Input Section */}
                {showTextInput && (
                    <div style={{
                        marginBottom: '20px',
                        position: 'relative',
                        zIndex: 1
                    }}>
                        <input
                            type="text"
                            value={textInputValue}
                            onChange={(e) => setTextInputValue(validateTextInput(e.target.value))}
                            placeholder={`Enter ${(challenge as any).measurementUnit || challenge.targetUnit}`}
                            style={{
                                width: '100%',
                                padding: '16px 20px',
                                borderRadius: '16px',
                                border: `2px solid ${theme.colors.primary}20`,
                                fontSize: '18px',
                                fontWeight: '600',
                                backgroundColor: `linear-gradient(135deg, ${theme.colors.background} 0%, ${theme.colors.surface} 100%)`,
                                color: theme.colors.text,
                                marginBottom: '12px',
                                boxShadow: `0 4px 16px ${theme.colors.primary}10`,
                                transition: 'all 0.3s ease'
                            }}
                        />
                        <div style={{
                            display: 'flex',
                            gap: '12px'
                        }}>
                            <button
                                style={{
                                    flex: 1,
                                    padding: '12px 20px',
                                    borderRadius: '12px',
                                    border: `2px solid ${theme.colors.border}`,
                                    background: `linear-gradient(135deg, ${theme.colors.surface} 0%, ${theme.colors.background} 100%)`,
                                    color: theme.colors.text,
                                    fontSize: '16px',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'translateY(-1px)';
                                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = 'none';
                                }}
                                onClick={() => setShowTextInput(false)}
                            >
                                Cancel
                            </button>
                            <button
                                style={{
                                    flex: 1,
                                    padding: '12px 20px',
                                    borderRadius: '12px',
                                    border: 'none',
                                    background: `linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.primary}DD 100%)`,
                                    color: 'white',
                                    fontSize: '16px',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    boxShadow: `0 4px 16px ${theme.colors.primary}30`,
                                    transition: 'all 0.3s ease'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'translateY(-1px)';
                                    e.currentTarget.style.boxShadow = `0 6px 20px ${theme.colors.primary}40`;
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = `0 4px 16px ${theme.colors.primary}30`;
                                }}
                                onClick={handleTextInputSubmit}
                            >
                                Set Value
                            </button>
                        </div>
                    </div>
                )}

                {/* Ultra-Posh Action Buttons Row */}
                <div style={{
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    gap: isMobile ? '8px' : '16px',
                    marginTop: isMobile ? '12px' : '20px',
                    position: 'relative',
                    zIndex: 1
                }}>
                    <button
                        style={{
                            flex: 1,
                            padding: isMobile ? '12px 16px' : '16px 24px',
                            borderRadius: '999px',
                            border: 'none',
                            backgroundImage: isUpdating
                                ? 'linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)'
                                : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            color: 'white',
                            fontSize: isMobile ? '13px' : '16px',
                            fontWeight: '600',
                            fontFamily: theme.fonts.primary,
                            cursor: isUpdating ? 'not-allowed' : 'pointer',
                            whiteSpace: 'nowrap',
                            minHeight: isMobile ? '44px' : '56px',
                            boxShadow: isUpdating
                                ? '0 4px 12px rgba(0, 0, 0, 0.1)'
                                : '0 8px 20px rgba(102, 126, 234, 0.35)',
                            transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                            position: 'relative',
                            overflow: 'hidden',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '6px',
                        }}
                        onMouseEnter={(e) => {
                            if (!isUpdating) {
                                e.currentTarget.style.transform = 'translateY(-1px)';
                                e.currentTarget.style.boxShadow = '0 12px 28px rgba(102, 126, 234, 0.45)';
                            }
                        }}
                        onMouseLeave={(e) => {
                            if (!isUpdating) {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = '0 8px 20px rgba(102, 126, 234, 0.35)';
                            }
                        }}
                        onClick={handleUpdateProgress}
                        disabled={isUpdating}
                    >
                        {!isUpdating && (
                            <div style={{
                                position: 'absolute',
                                top: '0',
                                left: '-100%',
                                width: '100%',
                                height: '100%',
                                background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.25), transparent)',
                                animation: 'shine 2s infinite'
                            }} />
                        )}
                        <span style={{ position: 'relative', zIndex: 1 }}>
                            {isUpdating ? '⏳ Updating...' : '🚀 Update Progress'}
                        </span>
                    </button>

                    {/* Ultra-Posh Auto-populate Health Data Button */}
                    {(challenge as any).measurementUnit === 'steps' || (challenge as any).measurementUnit === 'distance' || (challenge as any).measurementUnit === 'miles' || (challenge as any).measurementUnit === 'kilometers' || challenge.targetUnit === 'steps' || challenge.targetUnit === 'distance' || challenge.targetUnit === 'miles' || challenge.targetUnit === 'kilometers' ? (
                        <button
                            style={{
                                flex: 1,
                                padding: isMobile ? '12px 16px' : '16px 24px',
                                borderRadius: '16px',
                                border: 'none',
                                background: `linear-gradient(135deg, ${theme.colors.success} 0%, ${theme.colors.success}DD 100%)`,
                                color: 'white',
                                fontSize: isMobile ? '13px' : '16px',
                                fontWeight: '700',
                                cursor: 'pointer',
                                whiteSpace: 'nowrap',
                                minHeight: isMobile ? '44px' : '56px',
                                boxShadow: `0 8px 24px ${theme.colors.success}30`,
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                position: 'relative',
                                overflow: 'hidden'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-2px)';
                                e.currentTarget.style.boxShadow = `0 12px 32px ${theme.colors.success}40`;
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = `0 8px 24px ${theme.colors.success}30`;
                            }}
                            onClick={() => {
                                // Placeholder for auto-populate functionality
                                alert('Auto-populate functionality would go here');
                            }}
                        >
                            <div style={{
                                position: 'absolute',
                                top: '0',
                                left: '-100%',
                                width: '100%',
                                height: '100%',
                                background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)',
                                animation: 'shine 2s infinite'
                            }} />
                            <span style={{ position: 'relative', zIndex: 1 }}>
                                📱 Today
                            </span>
                        </button>
                    ) : null}

                    {/* Ultra-Posh Auto-populate Past Days Button */}
                    {(challenge as any).measurementUnit === 'steps' || (challenge as any).measurementUnit === 'distance' || (challenge as any).measurementUnit === 'miles' || (challenge as any).measurementUnit === 'kilometers' || challenge.targetUnit === 'steps' || (challenge as any).measurementUnit === 'miles' || (challenge as any).measurementUnit === 'kilometers' ? (
                        <button
                            style={{
                                flex: 1,
                                padding: isMobile ? '12px 16px' : '16px 24px',
                                borderRadius: '16px',
                                border: 'none',
                                background: `linear-gradient(135deg, ${theme.colors.warning} 0%, ${theme.colors.warning}DD 100%)`,
                                color: 'white',
                                fontSize: isMobile ? '13px' : '16px',
                                fontWeight: '700',
                                cursor: 'pointer',
                                whiteSpace: 'nowrap',
                                minHeight: isMobile ? '44px' : '56px',
                                boxShadow: `0 8px 24px ${theme.colors.warning}30`,
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                position: 'relative',
                                overflow: 'hidden'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-2px)';
                                e.currentTarget.style.boxShadow = `0 12px 32px ${theme.colors.warning}40`;
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = `0 8px 24px ${theme.colors.warning}30`;
                            }}
                            onClick={() => {
                                // Placeholder for auto-populate functionality
                                alert('Auto-populate past days functionality would go here');
                            }}
                        >
                            <div style={{
                                position: 'absolute',
                                top: '0',
                                left: '-100%',
                                width: '100%',
                                height: '100%',
                                background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)',
                                animation: 'shine 2s infinite'
                            }} />
                            <span style={{ position: 'relative', zIndex: 1 }}>
                                📅 Past 5 Days
                            </span>
                        </button>
                    ) : null}
                </div>
            </div>

            {/* Ultra-Posh Calendar */}
            <div style={{
                background: `linear-gradient(135deg, ${theme.colors.surface} 0%, ${theme.colors.surface}EE 100%)`,
                borderRadius: isMobile ? '12px' : '20px',
                padding: isMobile ? '12px' : '28px',
                marginBottom: isMobile ? '12px' : '32px',
                border: `1px solid ${theme.colors.border}`,
                position: 'relative',
                overflow: 'hidden',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)'
            }}>
                {/* Decorative Elements */}
                <div style={{
                    position: 'absolute',
                    top: '-30px',
                    left: '-30px',
                    width: '120px',
                    height: '120px',
                    background: `radial-gradient(circle, ${theme.colors.primary}06 0%, transparent 70%)`,
                    borderRadius: '50%'
                }} />
                <div style={{
                    position: 'absolute',
                    bottom: '-40px',
                    right: '-40px',
                    width: '160px',
                    height: '160px',
                    background: `radial-gradient(circle, ${theme.colors.secondary}04 0%, transparent 70%)`,
                    borderRadius: '50%'
                }} />

                <div style={{
                    position: 'relative',
                    zIndex: 1,
                    marginBottom: isMobile ? '16px' : '24px'
                }}>
                    <h3 style={{
                        fontSize: isMobile ? theme.typography.h3.fontSize : theme.typography.h2.fontSize,
                        fontWeight: theme.typography.h2.fontWeight,
                        background: `linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.primary}CC 100%)`,
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                        marginBottom: isMobile ? '4px' : '8px',
                        letterSpacing: theme.typography.h2.letterSpacing,
                        fontFamily: theme.fonts.display
                    }}>
                        Daily Progress Calendar
                    </h3>
                    <p style={{
                        fontSize: isMobile ? theme.typography.caption.fontSize : theme.typography.body.fontSize,
                        color: theme.colors.textSecondary,
                        fontWeight: theme.typography.body.fontWeight,
                        opacity: 0.8,
                        fontFamily: theme.fonts.secondary
                    }}>
                        Track your daily achievements and progress
                    </p>
                </div>

                {/* Ultra-Posh Month Navigation */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: isMobile ? '12px' : '20px',
                    position: 'relative',
                    zIndex: 1
                }}>
                    <button
                        style={{
                            width: isMobile ? '36px' : '48px',
                            height: isMobile ? '36px' : '48px',
                            borderRadius: isMobile ? '12px' : '16px',
                            border: 'none',
                            background: `linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.primary}DD 100%)`,
                            color: 'white',
                            fontSize: isMobile ? '16px' : '20px',
                            fontWeight: '800',
                            cursor: 'pointer',
                            boxShadow: `0 6px 20px ${theme.colors.primary}30`,
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            position: 'relative',
                            overflow: 'hidden'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = `0 8px 24px ${theme.colors.primary}40`;
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = `0 6px 20px ${theme.colors.primary}30`;
                        }}
                        onClick={handlePreviousMonth}
                    >
                        <div style={{
                            position: 'absolute',
                            top: '0',
                            left: '-100%',
                            width: '100%',
                            height: '100%',
                            background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)',
                            animation: 'shine 2s infinite'
                        }} />
                        <span style={{ position: 'relative', zIndex: 1 }}>‹</span>
                    </button>

                    <div style={{
                        background: `linear-gradient(135deg, ${theme.colors.background} 0%, ${theme.colors.surface} 100%)`,
                        borderRadius: isMobile ? '12px' : '16px',
                        padding: isMobile ? '8px 12px' : '12px 24px',
                        border: `2px solid ${theme.colors.primary}20`,
                        boxShadow: `0 4px 16px ${theme.colors.primary}10`
                    }}>
                        <h4 style={{
                            fontSize: isMobile ? theme.typography.h3.fontSize : theme.typography.h2.fontSize,
                            fontWeight: theme.typography.h3.fontWeight,
                            color: theme.colors.text,
                            margin: 0,
                            letterSpacing: theme.typography.h3.letterSpacing,
                            fontFamily: theme.fonts.display
                        }}>
                            {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                        </h4>
                    </div>

                    <button
                        style={{
                            width: isMobile ? '36px' : '48px',
                            height: isMobile ? '36px' : '48px',
                            borderRadius: isMobile ? '12px' : '16px',
                            border: 'none',
                            background: `linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.primary}DD 100%)`,
                            color: 'white',
                            fontSize: isMobile ? '16px' : '20px',
                            fontWeight: '800',
                            cursor: 'pointer',
                            boxShadow: `0 6px 20px ${theme.colors.primary}30`,
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            position: 'relative',
                            overflow: 'hidden'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = `0 8px 24px ${theme.colors.primary}40`;
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = `0 6px 20px ${theme.colors.primary}30`;
                        }}
                        onClick={handleNextMonth}
                    >
                        <div style={{
                            position: 'absolute',
                            top: '0',
                            left: '-100%',
                            width: '100%',
                            height: '100%',
                            background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)',
                            animation: 'shine 2s infinite'
                        }} />
                        <span style={{ position: 'relative', zIndex: 1 }}>›</span>
                    </button>
                </div>

                {/* Ultra-Posh Day Headers */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(7, 1fr)',
                    gap: isMobile ? '4px' : '6px',
                    marginBottom: isMobile ? '8px' : '12px',
                    position: 'relative',
                    zIndex: 1
                }}>
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                        <div key={day} style={{
                            textAlign: 'center',
                            padding: isMobile ? '6px 2px' : '8px 4px',
                            borderRadius: '8px',
                            background: `linear-gradient(135deg, ${theme.colors.primary}08 0%, ${theme.colors.primary}04 100%)`,
                            border: `1px solid ${theme.colors.primary}15`
                        }}>
                            <span style={{
                                fontSize: isMobile ? '11px' : '13px',
                                fontWeight: '600',
                                color: theme.colors.text,
                                letterSpacing: '0.5px'
                            }}>
                                {day}
                            </span>
                        </div>
                    ))}
                </div>

                {/* Ultra-Posh Calendar Grid */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(7, 1fr)',
                    gap: isMobile ? '4px' : '6px',
                    position: 'relative',
                    zIndex: 1
                }}>
                    {calendarData.map((day) => (
                        <div key={day.date} style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            padding: isMobile ? '2px' : '4px'
                        }}>
                            <div
                                style={{
                                    width: isMobile ? '32px' : '40px',
                                    height: isMobile ? '32px' : '40px',
                                    borderRadius: isMobile ? '8px' : '12px',
                                    border: '2px solid',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: isMobile ? '12px' : '14px',
                                    fontWeight: '600',
                                    cursor: !day.isPadding && isPastOrToday(new Date(day.date)) ? 'pointer' : 'default',
                                    background: day.isPadding
                                        ? 'transparent'
                                        : day.status === 'completed'
                                            ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                                            : day.status === 'partial'
                                                ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
                                                : day.status === 'missed'
                                                    ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
                                                    : `linear-gradient(135deg, ${theme.colors.background} 0%, ${theme.colors.surface} 100%)`,
                                    borderColor: day.isPadding
                                        ? 'transparent'
                                        : day.status === 'completed'
                                            ? '#059669'
                                            : day.status === 'partial'
                                                ? '#d97706'
                                                : day.status === 'missed'
                                                    ? '#dc2626'
                                                    : theme.colors.border,
                                    color: day.isPadding
                                        ? theme.colors.textSecondary
                                        : (day.status === 'completed' || day.status === 'partial' || day.status === 'missed')
                                            ? '#ffffff'
                                            : theme.colors.text,
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    boxShadow: day.isPadding
                                        ? 'none'
                                        : day.status === 'completed'
                                            ? '0 4px 12px rgba(16, 185, 129, 0.3)'
                                            : day.status === 'partial'
                                                ? '0 4px 12px rgba(245, 158, 11, 0.3)'
                                                : day.status === 'missed'
                                                    ? '0 4px 12px rgba(239, 68, 68, 0.3)'
                                                    : `0 2px 8px ${theme.colors.primary}10`,
                                    position: 'relative',
                                    overflow: 'hidden'
                                }}
                                onMouseEnter={(e) => {
                                    if (!day.isPadding && isPastOrToday(new Date(day.date))) {
                                        e.currentTarget.style.transform = 'translateY(-2px) scale(1.05)';
                                        e.currentTarget.style.boxShadow = day.status === 'completed'
                                            ? '0 8px 20px rgba(16, 185, 129, 0.4)'
                                            : day.status === 'partial'
                                                ? '0 8px 20px rgba(245, 158, 11, 0.4)'
                                                : day.status === 'missed'
                                                    ? '0 8px 20px rgba(239, 68, 68, 0.4)'
                                                    : `0 6px 16px ${theme.colors.primary}20`;
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (!day.isPadding && isPastOrToday(new Date(day.date))) {
                                        e.currentTarget.style.transform = 'translateY(0) scale(1)';
                                        e.currentTarget.style.boxShadow = day.status === 'completed'
                                            ? '0 4px 12px rgba(16, 185, 129, 0.3)'
                                            : day.status === 'partial'
                                                ? '0 4px 12px rgba(245, 158, 11, 0.3)'
                                                : day.status === 'missed'
                                                    ? '0 4px 12px rgba(239, 68, 68, 0.3)'
                                                    : `0 2px 8px ${theme.colors.primary}10`;
                                    }
                                }}
                                onClick={() => handleDayClick(day)}
                            >
                                {/* Shine effect for completed/partial days */}
                                {!day.isPadding && (day.status === 'completed' || day.status === 'partial') && (
                                    <div style={{
                                        position: 'absolute',
                                        top: '0',
                                        left: '-100%',
                                        width: '100%',
                                        height: '100%',
                                        background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)',
                                        animation: 'shine 3s infinite'
                                    }} />
                                )}
                                <span style={{ position: 'relative', zIndex: 1 }}>
                                    {day.day}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Legend */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-around',
                    marginTop: isMobile ? '12px' : '16px',
                    gap: isMobile ? '4px' : '0'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <div style={{
                            width: isMobile ? '8px' : '12px',
                            height: isMobile ? '8px' : '12px',
                            borderRadius: '50%',
                            backgroundColor: '#10b981',
                            marginRight: isMobile ? '4px' : '8px'
                        }} />
                        <span style={{
                            fontSize: isMobile ? '10px' : '12px',
                            color: theme.colors.textSecondary
                        }}>
                            Completed
                        </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <div style={{
                            width: isMobile ? '8px' : '12px',
                            height: isMobile ? '8px' : '12px',
                            borderRadius: '50%',
                            backgroundColor: '#f59e0b',
                            marginRight: isMobile ? '4px' : '8px'
                        }} />
                        <span style={{
                            fontSize: isMobile ? '10px' : '12px',
                            color: theme.colors.textSecondary
                        }}>
                            Partial
                        </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <div style={{
                            width: isMobile ? '8px' : '12px',
                            height: isMobile ? '8px' : '12px',
                            borderRadius: '50%',
                            backgroundColor: '#ef4444',
                            marginRight: isMobile ? '4px' : '8px'
                        }} />
                        <span style={{
                            fontSize: isMobile ? '10px' : '12px',
                            color: theme.colors.textSecondary
                        }}>
                            Missed
                        </span>
                    </div>
                </div>

                {/* Grace Period Info */}
                <div style={{
                    marginTop: isMobile ? '12px' : '16px',
                    padding: isMobile ? '8px' : '12px',
                    backgroundColor: theme.colors.warning + '10',
                    borderRadius: '8px',
                    border: `1px solid ${theme.colors.warning + '30'}`
                }}>
                    <p style={{
                        fontSize: isMobile ? '10px' : '12px',
                        color: theme.colors.textSecondary,
                        margin: 0,
                        textAlign: 'center'
                    }}>
                        💡 Click on any day in the past week to edit your progress (5-day grace period)
                    </p>
                </div>
            </div>

            {/* Ultra-Posh Action Buttons */}
            <div style={{
                background: `linear-gradient(135deg, ${theme.colors.surface} 0%, ${theme.colors.surface}EE 100%)`,
                borderRadius: isMobile ? '12px' : '20px',
                padding: isMobile ? '12px' : '28px',
                marginBottom: isMobile ? '12px' : '32px',
                border: `1px solid ${theme.colors.border}`,
                position: 'relative',
                overflow: 'hidden',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)'
            }}>
                {/* Decorative Elements */}
                <div style={{
                    position: 'absolute',
                    top: '-40px',
                    right: '-40px',
                    width: '120px',
                    height: '120px',
                    background: `radial-gradient(circle, ${theme.colors.primary}06 0%, transparent 70%)`,
                    borderRadius: '50%'
                }} />
                <div style={{
                    position: 'absolute',
                    bottom: '-30px',
                    left: '-30px',
                    width: '100px',
                    height: '100px',
                    background: `radial-gradient(circle, ${theme.colors.error}04 0%, transparent 70%)`,
                    borderRadius: '50%'
                }} />

                <div style={{
                    position: 'relative',
                    zIndex: 1
                }}>
                    <button
                        style={{
                            width: '100%',
                            padding: isMobile ? '12px 16px' : '16px 24px',
                            borderRadius: '999px',
                            border: 'none',
                            backgroundImage: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            color: 'white',
                            fontSize: isMobile ? theme.typography.button.fontSize : theme.typography.button.fontSize,
                            fontWeight: theme.typography.button.fontWeight,
                            fontFamily: theme.fonts.primary,
                            lineHeight: theme.typography.button.lineHeight,
                            marginBottom: isMobile ? '12px' : '16px',
                            cursor: 'pointer',
                            boxShadow: '0 8px 20px rgba(102, 126, 234, 0.35)',
                            transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                            position: 'relative',
                            overflow: 'hidden',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '6px',
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-1px)';
                            e.currentTarget.style.boxShadow = '0 12px 28px rgba(102, 126, 234, 0.45)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 8px 20px rgba(102, 126, 234, 0.35)';
                        }}
                        onClick={onViewLeaderboard}
                    >
                        <div style={{
                            position: 'absolute',
                            top: '0',
                            left: '-100%',
                            width: '100%',
                            height: '100%',
                            background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.25), transparent)',
                            animation: 'shine 2s infinite'
                        }} />
                        <span style={{ position: 'relative', zIndex: 1 }}>
                            🏆 View Leaderboard
                        </span>
                    </button>

                    <button
                        style={{
                            width: '100%',
                            padding: isMobile ? '12px 16px' : '16px 24px',
                            borderRadius: '999px',
                            border: 'none',
                            backgroundImage: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                            color: 'white',
                            fontSize: isMobile ? theme.typography.button.fontSize : theme.typography.button.fontSize,
                            fontWeight: theme.typography.button.fontWeight,
                            fontFamily: theme.fonts.primary,
                            lineHeight: theme.typography.button.lineHeight,
                            cursor: 'pointer',
                            boxShadow: '0 8px 20px rgba(239, 68, 68, 0.35)',
                            transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                            position: 'relative',
                            overflow: 'hidden',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '6px',
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-1px)';
                            e.currentTarget.style.boxShadow = '0 12px 28px rgba(239, 68, 68, 0.45)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 8px 20px rgba(239, 68, 68, 0.35)';
                        }}
                        onClick={onDropChallenge}
                    >
                        <div style={{
                            position: 'absolute',
                            top: '0',
                            left: '-100%',
                            width: '100%',
                            height: '100%',
                            background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.25), transparent)',
                            animation: 'shine 2s infinite'
                        }} />
                        <span style={{ position: 'relative', zIndex: 1 }}>
                            🚪 Drop Challenge
                        </span>
                    </button>
                </div>
            </div>

            {/* Day Detail View */}
            {showDayDetail && selectedDate && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000,
                    padding: '20px'
                }}>
                    <div style={{
                        backgroundColor: theme.colors.surface,
                        borderRadius: '16px',
                        padding: '24px',
                        maxWidth: '400px',
                        width: '100%',
                        maxHeight: '80vh',
                        overflow: 'auto',
                        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
                    }}>
                        {/* Header */}
                        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                            <div style={{ fontSize: '48px', marginBottom: '12px' }}>
                                📅
                            </div>
                            <h3 style={{
                                fontSize: '20px',
                                fontWeight: '600',
                                color: theme.colors.text,
                                marginBottom: '8px'
                            }}>
                                {challenge.title}
                            </h3>
                            <p style={{
                                fontSize: '14px',
                                color: theme.colors.textSecondary,
                                marginBottom: '8px'
                            }}>
                                {selectedDate.toLocaleDateString('en-US', {
                                    month: 'short',
                                    day: 'numeric',
                                    weekday: 'short'
                                })}
                            </p>
                        </div>

                        {/* Content */}
                        <div>
                            {/* Current Status */}
                            <div style={{
                                backgroundColor: theme.colors.primary + '10',
                                borderRadius: '8px',
                                padding: '16px',
                                marginBottom: '20px'
                            }}>
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: '8px'
                                }}>
                                    <span style={{
                                        fontSize: '14px',
                                        color: theme.colors.textSecondary
                                    }}>
                                        Current Value
                                    </span>
                                    <span style={{
                                        fontSize: '16px',
                                        fontWeight: '600',
                                        color: theme.colors.primary
                                    }}>
                                        {formatValue(getExistingValueForDate(selectedDate), challenge.metric, (challenge as any).measurementUnit || challenge.targetUnit)}
                                    </span>
                                </div>
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center'
                                }}>
                                    <span style={{
                                        fontSize: '14px',
                                        color: theme.colors.textSecondary
                                    }}>
                                        Target
                                    </span>
                                    <span style={{
                                        fontSize: '16px',
                                        fontWeight: '600',
                                        color: theme.colors.text
                                    }}>
                                        {formatValue(challenge.targetValue, challenge.metric, challenge.targetUnit)}
                                    </span>
                                </div>
                            </div>

                            {/* Value Selector with Text Input Toggle for Previous Days */}
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                marginBottom: '20px'
                            }}>
                                <button
                                    style={{
                                        width: '48px',
                                        height: '48px',
                                        borderRadius: '8px',
                                        border: 'none',
                                        backgroundColor: theme.colors.primary,
                                        color: theme.colors.primaryText,
                                        fontSize: '20px',
                                        fontWeight: 'bold',
                                        cursor: 'pointer'
                                    }}
                                    onClick={() => {
                                        const currentValue = getExistingValueForDate(selectedDate);
                                        const newValue = Math.max(0, currentValue - getStepSize(challenge.metric));
                                        // Update the input value for the +/- interface
                                        const input = document.querySelector('input[type="number"]') as HTMLInputElement;
                                        if (input) input.value = newValue.toString();
                                    }}
                                >
                                    -
                                </button>

                                <div style={{
                                    flex: 1,
                                    padding: '12px',
                                    borderRadius: '8px',
                                    border: `1px solid ${theme.colors.border}`,
                                    backgroundColor: theme.colors.background,
                                    textAlign: 'center'
                                }}>
                                    <span style={{
                                        fontSize: '18px',
                                        fontWeight: '600',
                                        color: theme.colors.text
                                    }}>
                                        {formatValue(getExistingValueForDate(selectedDate), challenge.metric, (challenge as any).measurementUnit || challenge.targetUnit)}
                                    </span>
                                </div>

                                <button
                                    style={{
                                        width: '48px',
                                        height: '48px',
                                        borderRadius: '8px',
                                        border: 'none',
                                        backgroundColor: theme.colors.primary,
                                        color: theme.colors.primaryText,
                                        fontSize: '20px',
                                        fontWeight: 'bold',
                                        cursor: 'pointer'
                                    }}
                                    onClick={() => {
                                        const currentValue = getExistingValueForDate(selectedDate);
                                        const newValue = Math.min(((challenge as any).measurementTarget || challenge.targetValue) * 2, currentValue + getStepSize(challenge.metric));
                                        // Update the input value for the +/- interface
                                        const input = document.querySelector('input[type="number"]') as HTMLInputElement;
                                        if (input) input.value = newValue.toString();
                                    }}
                                >
                                    +
                                </button>

                                {/* Text Input Toggle Button - positioned next to + button */}
                                <button
                                    style={{
                                        width: '48px',
                                        height: '48px',
                                        borderRadius: '24px',
                                        border: 'none',
                                        backgroundColor: theme.colors.border,
                                        color: theme.colors.text,
                                        fontSize: '16px',
                                        fontWeight: '600',
                                        cursor: 'pointer'
                                    }}
                                    onClick={() => {
                                        if (showDayTextInput) {
                                            setShowDayTextInput(false);
                                            setDayTextInputValue('');
                                        } else {
                                            setShowDayTextInput(true);
                                            setDayTextInputValue(getExistingValueForDate(selectedDate).toString());
                                        }
                                    }}
                                    title={showDayTextInput ? 'Switch to +/- buttons' : 'Enter value manually'}
                                >
                                    {showDayTextInput ? '↺' : '✏️'}
                                </button>
                            </div>

                            {/* Text Input for Previous Days */}
                            {showDayTextInput && (
                                <div style={{ marginBottom: '20px' }}>
                                    <input
                                        type="text"
                                        value={dayTextInputValue}
                                        onChange={(e) => setDayTextInputValue(validateDayTextInput(e.target.value))}
                                        placeholder={`Enter ${(challenge as any).measurementUnit || challenge.targetUnit}`}
                                        style={{
                                            width: '100%',
                                            padding: '12px',
                                            borderRadius: '8px',
                                            border: `1px solid ${theme.colors.border}`,
                                            fontSize: '16px',
                                            backgroundColor: theme.colors.background,
                                            color: theme.colors.text,
                                            marginBottom: '8px'
                                        }}
                                    />
                                    <div style={{
                                        display: 'flex',
                                        gap: '8px'
                                    }}>
                                        <button
                                            style={{
                                                flex: 1,
                                                padding: '8px 16px',
                                                borderRadius: '8px',
                                                border: `1px solid ${theme.colors.border}`,
                                                backgroundColor: theme.colors.surface,
                                                color: theme.colors.text,
                                                fontSize: '14px',
                                                fontWeight: '500',
                                                cursor: 'pointer'
                                            }}
                                            onClick={() => setShowDayTextInput(false)}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            style={{
                                                flex: 1,
                                                padding: '8px 16px',
                                                borderRadius: '8px',
                                                border: 'none',
                                                backgroundColor: theme.colors.primary,
                                                color: theme.colors.primaryText,
                                                fontSize: '14px',
                                                fontWeight: '500',
                                                cursor: 'pointer'
                                            }}
                                            onClick={() => {
                                                const value = handleDayTextInputSubmit();
                                                if (value !== null && selectedDate) {
                                                    handleSaveDayProgress(selectedDate, value);
                                                }
                                            }}
                                        >
                                            Set
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Number Input for +/- Interface */}
                            {!showDayTextInput && (
                                <div style={{ marginBottom: '20px' }}>
                                    <label style={{
                                        display: 'block',
                                        fontSize: '14px',
                                        fontWeight: '500',
                                        color: theme.colors.text,
                                        marginBottom: '8px'
                                    }}>
                                        Or enter value directly:
                                    </label>
                                    <input
                                        type="number"
                                        defaultValue={getExistingValueForDate(selectedDate).toString()}
                                        min="0"
                                        max={((challenge as any).measurementTarget || challenge.targetValue) * 2}
                                        step="1"
                                        style={{
                                            width: '100%',
                                            padding: '12px',
                                            borderRadius: '8px',
                                            border: `1px solid ${theme.colors.border}`,
                                            fontSize: '16px',
                                            backgroundColor: theme.colors.surface,
                                            color: theme.colors.text
                                        }}
                                        placeholder={`Enter value in ${(challenge as any).measurementUnit || challenge.targetUnit}`}
                                    />
                                </div>
                            )}
                        </div>

                        {/* Action Buttons */}
                        <div style={{
                            display: 'flex',
                            gap: '12px',
                            marginTop: '24px'
                        }}>
                            <button
                                onClick={handleCancelDayDetail}
                                style={{
                                    flex: 1,
                                    padding: '12px',
                                    borderRadius: '8px',
                                    border: `1px solid ${theme.colors.border}`,
                                    backgroundColor: theme.colors.surface,
                                    color: theme.colors.text,
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    cursor: 'pointer'
                                }}
                            >
                                Cancel
                            </button>

                            <button
                                onClick={() => {
                                    const input = document.querySelector('input[type="number"]') as HTMLInputElement;
                                    if (input && selectedDate) {
                                        const value = parseFloat(input.value);
                                        if (!isNaN(value) && value >= 0) {
                                            handleSaveDayProgress(selectedDate, value);
                                        }
                                    }
                                }}
                                style={{
                                    flex: 1,
                                    padding: '12px 20px',
                                    borderRadius: '999px',
                                    border: 'none',
                                    backgroundImage: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    color: 'white',
                                    fontSize: '14px',
                                    fontWeight: '600',
                                    fontFamily: theme.fonts.primary,
                                    cursor: 'pointer',
                                    boxShadow: '0 8px 20px rgba(102, 126, 234, 0.35)',
                                    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                                    position: 'relative',
                                    overflow: 'hidden',
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'translateY(-1px)';
                                    e.currentTarget.style.boxShadow = '0 12px 28px rgba(102, 126, 234, 0.45)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = '0 8px 20px rgba(102, 126, 234, 0.35)';
                                }}
                            >
                                <div style={{
                                    position: 'absolute',
                                    top: '0',
                                    left: '-100%',
                                    width: '100%',
                                    height: '100%',
                                    background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.25), transparent)',
                                    animation: 'shine 2s infinite'
                                }} />
                                <span style={{ position: 'relative', zIndex: 1 }}>
                                    Save Progress
                                </span>
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Video / Audio Player Modal */}
            {showVideoPlayer && ((challenge as any)?.videoUrl || (challenge as any)?.audioUrl) && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.9)',
                    zIndex: 1000,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}>
                    {(challenge as any)?.videoUrl ? (
                        <VideoPlayer
                            videoUrl={(challenge as any).videoUrl}
                            onClose={handleVideoClose}
                            autoPlay={!hasShownWelcomeVideo}
                            showControls={true}
                            className="w-full h-full"
                        />
                    ) : (
                        <div style={{
                            backgroundColor: '#000000',
                            padding: '24px',
                            borderRadius: '16px',
                            maxWidth: '480px',
                            width: '100%',
                            boxShadow: '0 10px 40px rgba(0,0,0,0.6)',
                            color: '#ffffff',
                        }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>
                                    Challenge Audio
                                </h3>
                                <button
                                    onClick={handleVideoClose}
                                    style={{
                                        background: 'transparent',
                                        border: 'none',
                                        color: '#ffffff',
                                        fontSize: '20px',
                                        cursor: 'pointer',
                                    }}
                                    aria-label="Close audio"
                                >
                                    ×
                                </button>
                            </div>
                            <p style={{ marginBottom: '12px', fontSize: '14px', opacity: 0.85 }}>
                                Listen to the challenge instructions or motivation.
                            </p>
                            <audio
                                src={(challenge as any).audioUrl}
                                controls
                                autoPlay={!hasShownWelcomeVideo}
                                style={{ width: '100%' }}
                            />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}; 
